import Glide from "@glidejs/glide";

import "@glidejs/glide/dist/css/glide.core.min.css";
import "@glidejs/glide/dist/css/glide.theme.min.css";

/***************************************************************************************************
 ** Animation Helpers
 ***************************************************************************************************/

const isVisible = (element) => {
  return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
};

const onTransitionEnd = (element, property, duration, callback) => {
  let isSettled = false;

  const handleCallback = () => {
    if (isSettled) return;
    isSettled = true;

    element.removeEventListener("transitionend", handleTransitionEnd);
    callback();
  };

  const handleTransitionEnd = (event) => {
    if (event.target === element && event.propertyName === property) handleCallback();
  };

  element.addEventListener("transitionend", handleTransitionEnd);
  setTimeout(handleCallback, duration + 50);
};

const fadeIn = (element, duration) => {
  element.style.opacity = 0;
  element.style.removeProperty("display");
  element.style.transition = `opacity ${duration}ms`;

  requestAnimationFrame(() => {
    element.style.opacity = 1;

    onTransitionEnd(element, "opacity", duration, () => {
      element.style.removeProperty("transition");
      element.style.removeProperty("opacity");
    });
  });
};

const slideDown = (element, duration) => {
  element.style.removeProperty("display");

  const computedDisplay = window.getComputedStyle(element).display;
  const isHidden = computedDisplay === "none";
  const intendedDisplay = element.dataset.slideDisplay || "block";

  element.style.display = isHidden ? intendedDisplay : computedDisplay;
  element.style.boxSizing = "border-box";

  const targetHeight = element.offsetHeight;

  element.style.overflow = "hidden";
  element.style.height = "0px";
  element.style.transition = `height ${duration}ms`;

  requestAnimationFrame(() => {
    element.style.height = `${targetHeight}px`;

    onTransitionEnd(element, "height", duration, () => {
      element.style.removeProperty("height");
      element.style.removeProperty("overflow");
      element.style.removeProperty("transition");
      element.style.removeProperty("box-sizing");
    });
  });
};

const slideUp = (element, duration) => {
  element.style.boxSizing = "border-box";
  element.style.height = `${element.offsetHeight}px`;
  element.style.overflow = "hidden";
  element.style.transition = `height ${duration}ms`;

  requestAnimationFrame(() => {
    element.style.height = "0px";

    onTransitionEnd(element, "height", duration, () => {
      element.style.removeProperty("display");
      element.style.removeProperty("height");
      element.style.removeProperty("overflow");
      element.style.removeProperty("transition");
      element.style.removeProperty("box-sizing");
    });
  });
};

const slideToggle = (element, duration = 400) => {
  if (isVisible(element)) {
    slideUp(element, duration);
  } else {
    slideDown(element, duration);
  }
};

/***************************************************************************************************
 ** Glide
 ***************************************************************************************************/

const initializeGlide = () => {
  const glider = document.querySelector(".glide");
  if (!glider) return;

  const isPIHHealth = document.location.host.split(".")[0] === "pihhealth";
  const storiesCount = parseInt(glider.dataset.storiesCount, 10) || 0;

  new Glide(".glide", {
    type: "carousel",
    perView: isPIHHealth ? 1 : 2,
    autoplay: storiesCount <= 2 ? false : 7000,
    hoverpause: true,
    gap: isPIHHealth ? 100 : 30,
    breakpoints: {
      900: {
        perView: 1,
      },
    },
  }).mount();
};

const fadeInGlide = () => {
  const glider = document.querySelector(".glide");
  if (!glider) return;

  glider.style.display = "none";

  setTimeout(() => {
    fadeIn(glider, 600);
  }, 200);
};

/**************************************************************************************************/

const positionMobileNavbar = () => {
  const siteHeader = document.querySelector(".header");
  const mobileNavbar = document.querySelector(".secondary-header");

  if (siteHeader && mobileNavbar) {
    const siteHeaderHeight = siteHeader.offsetHeight;

    mobileNavbar.style.top = `${siteHeaderHeight}px`;
  }
};

const handleMobileNavbarToggle = (event) => {
  event.preventDefault();

  const toggleElement = document.querySelector(".header__nav-toggle");
  const secondaryHeader = document.querySelector(".secondary-header");
  if (!secondaryHeader) return;

  const isExpanded = !isVisible(secondaryHeader);

  if (isExpanded && toggleElement && isVisible(toggleElement)) {
    positionMobileNavbar();
  }

  slideToggle(secondaryHeader, 100);

  document.querySelectorAll(".header__nav-toggle").forEach((element) => {
    element.setAttribute("aria-expanded", String(isExpanded));
  });
};

const handleMobileNavbarKeyDown = (event) => {
  if (event.key === "Enter" || event.key === " ") {
    handleMobileNavbarToggle(event);
  }
};

const initializeMobileNavbarToggle = () => {
  document.querySelectorAll(".header__nav-toggle").forEach((toggleElement) => {
    toggleElement.removeEventListener("click", handleMobileNavbarToggle);
    toggleElement.removeEventListener("keydown", handleMobileNavbarKeyDown);
    toggleElement.addEventListener("click", handleMobileNavbarToggle);
    toggleElement.addEventListener("keydown", handleMobileNavbarKeyDown);
  });
};

const handleMobileFiltersToggle = (event) => {
  event.preventDefault();

  const filtersForm = document.querySelector(".filter-bar__form");
  if (filtersForm) slideToggle(filtersForm);
};

const initializeMobileFiltersToggle = () => {
  document.querySelectorAll(".filter-bar__toggle").forEach((toggleElement) => {
    toggleElement.removeEventListener("click", handleMobileFiltersToggle);
    toggleElement.addEventListener("click", handleMobileFiltersToggle);
  });
};

/***************************************************************************************************
 ** Turbo Load
 ***************************************************************************************************/

document.addEventListener("turbo:load", () => {
  initializeGlide();
  fadeInGlide();

  positionMobileNavbar();
  initializeMobileNavbarToggle();
  initializeMobileFiltersToggle();
});
